import React from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { Category, categories } from "@quest_editor/mocks";
import { nanoid } from "nanoid";
import debounce from "lodash/debounce";

const searchCategories = (input: string): Category[] => {
  if (!input) return [];

  return categories.filter(
    (c) =>
      c.major.includes(input) ||
      c.middle.includes(input) ||
      c.small.includes(input) ||
      c.detail.includes(input),
  );
};

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (selected: Category[]) => void;
  inputValue: string;
  existingRows: Category[];
}

const CategorySearchDialog = ({
  open,
  onClose,
  onAdd,
  inputValue,
  existingRows,
}: SearchDialogProps) => {
  const { t } = useTranslation("questEditor");
  const [input, setInput] = React.useState(inputValue);
  const [results, setResults] = React.useState<Category[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<Category[]>([]);

  const performSearch = (word: string) => {
    const searchResults = searchCategories(word);
    setResults(searchResults);
  };

  const debouncedSearch = React.useMemo(
    () => debounce((word: string) => performSearch(word), 300),
    [],
  );

  React.useEffect(() => {
    if (open) {
      setInput(inputValue);
      setSelectedItems([]);
      performSearch(inputValue);
    }
  }, [open, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);
    debouncedSearch(val);
  };

  const isExisting = (item: Category) =>
    existingRows.some(
      (e) =>
        e.major === item.major &&
        e.middle === item.middle &&
        e.small === item.small &&
        e.detail === item.detail,
    );

  const handleToggle = (item: Category) => {
    if (isExisting(item)) return;
    const isSelected = selectedItems.some((i: Category) => i.id === item.id);
    if (isSelected) {
      setSelectedItems((prev: Category[]) =>
        prev.filter((i) => i.id !== item.id),
      );
    } else {
      setSelectedItems((prev: Category[]) => [...prev, item]);
    }
  };

  const handleConfirm = () => {
    const itemsToAdd = selectedItems.map((item: Category) => ({
      ...item,
      id: nanoid(),
    }));
    onAdd(itemsToAdd);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: "bold", pb: 1 }}>
        {t("common_field.category.dialog.title")}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          label={t("common_field.category.dialog.label")}
          value={input}
          onChange={handleInputChange}
          autoFocus
          sx={{ mb: 2, mt: 1 }}
        />

        <Box sx={{ minHeight: 250 }}>
          <List dense>
            {results.length > 0 ? (
              results.map((item) => (
                <ListItem key={item.id} disablePadding divider>
                  <ListItemButton
                    onClick={() => handleToggle(item)}
                    sx={{
                      whiteSpace: "nowrap",
                      px: 2,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={
                          isExisting(item) ||
                          selectedItems.some((i) => i.id === item.id)
                        }
                        disabled={isExisting(item)}
                        disableRipple
                      />
                    </ListItemIcon>

                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexGrow: 1 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {t("common_field.category.label.major", {
                          name: item.major,
                        })}
                      </Typography>
                      <ChevronRightIcon
                        sx={{ color: "action.disabled", fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {t("common_field.category.label.middle", {
                          name: item.middle,
                        })}
                      </Typography>
                      <ChevronRightIcon
                        sx={{ color: "action.disabled", fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {t("common_field.category.label.small", {
                          name: item.small,
                        })}
                      </Typography>
                      <ChevronRightIcon
                        sx={{ color: "action.disabled", fontSize: 18 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                      >
                        {t("common_field.category.label.detail", {
                          name: item.detail,
                        })}
                      </Typography>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 5 }}
              >
                {t("common_field.category.dialog.nodata", {
                  name: input,
                })}
              </Typography>
            )}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <BackButton onClick={onClose}>
          {t("common_field.category.dialog.button.cancel")}
        </BackButton>
        <ActionButton
          onClick={handleConfirm}
          disabled={selectedItems.length === 0}
        >
          {t("common_field.category.dialog.button.add", {
            count: selectedItems.length,
          })}
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default CategorySearchDialog;
